import {
    Connection,
    ConnectionState
} from "./connections";

export type StreamListener = (data: unknown, hash: string) => void;

import Debug from 'debug';
const log = Debug('judging-sdk:Streaming');

type StreamCacheEntry = {
    data?: any,
    hash: string
};

class StreamSubscription {
    precache: boolean;
    initial: boolean;
    cache?: StreamCacheEntry;
    gcTimer: number | undefined;
    private listeners: StreamListener[];

    getListenerCount() {
        return this.listeners.length;
    }

    constructor() {
        this.precache = false;
        this.initial = false;
        this.listeners = [];
    }

    push(listener: StreamListener) {
        this.listeners.push(listener);

        if (this.cache) {
            try {
                listener(this.cache.data, this.cache.hash);
            } catch (e) {
                console.error(e);
            }
        }
    }

    pop(listener: StreamListener) {
        const index = this.listeners.indexOf(listener);
        if (index >= 0) {
            this.listeners.splice(index, 1);
        }

        return this.listeners.length > 0;
    }

    publish(data: unknown, hash: string) {
        this.cache = { data, hash };
        for (const listener of this.listeners) {
            try {
                listener(data, hash);
            } catch (e) {
                console.error(e);
            }
        }
    }
}

function zipObject(props : string[], values : unknown[]) {
    return props.reduce((prev, prop, i) => {
        return Object.assign(prev, { [prop]: values[i] });
    }, {});
}

function uncompress(data : unknown) {
    if (data == null || typeof data !== 'object') {
        return data;
    }

    const compressedData = data as { keys?: unknown; rows?: unknown };
    if (!Array.isArray(compressedData.keys) || !Array.isArray(compressedData.rows)) {
        return data;
    }

    return compressedData.rows.map((row) =>
        Array.isArray(row) ? zipObject(compressedData.keys as string[], row) : row
    );
}

interface StreamHeader {
    url: string;
    hash: string;
    data: unknown;
}

export class StreamHandler {
    private _subscriptions : Map<string,StreamSubscription>;
    private static _instance : StreamHandler | null;
    private _connected : boolean;
    private _boundSocket : SocketIOClient.Socket | null;
    private readonly _streamListener : (response: unknown) => void;
    static GC_TIMEOUT = 5000;

    constructor() {
        this._subscriptions = new Map<string,StreamSubscription>();
        this._connected = false;
        this._boundSocket = null;
        this._streamListener = (response: unknown) => {
            this.onData(response);
        };
        Connection.get().addListener(state => {
            const connected = state === ConnectionState.Connected;
            if (connected === this._connected) {
                return;
            }

            this._connected = connected;
            if (connected) {
                this.onConnected();
            } else {
                this.onDisconnected();
            }
        });
    }

    static get() {
        if (!StreamHandler._instance) {
            StreamHandler._instance = new StreamHandler();
        }
        return StreamHandler._instance;
    }

    subscribe(url : string, listener: StreamListener) {
        let subscription = this._subscriptions.get(url);
        if (!subscription) {
            subscription = new StreamSubscription();
            this._subscriptions.set(url, subscription);

            log(`Adding new subscription to ${url}`);

            if (this._connected) {
                Connection.get().send("stream", {
                    url
                }).then(response => {
                    this.onData(response);
                }).catch(err => {
                    log(`Failed to subscribe to ${url}: ${err}`);
                });
            }
        } else {
            if (subscription.gcTimer) {
                log(`Aborted garbage collection for ${url}`);

                clearTimeout(subscription.gcTimer);
                delete subscription.gcTimer;
            }
        }

        subscription.push(listener);

        return () => {
            this._unsubscribe(url, listener);
        }
    }

    private _unsubscribe(url: string, listener: StreamListener) {
        const subscription = this._subscriptions.get(url);
        if (subscription) {
            if (!subscription.pop(listener)) {
                subscription.gcTimer = window.setTimeout(() => {
                    this._remove(url);
                }, StreamHandler.GC_TIMEOUT);
            }
        }
    }

    private _remove(url: string) {
        this._subscriptions.delete(url);

        log(`Removed subscription for ${url}`);

        if (this._connected) {
            Connection.get().send("unstream", {
                url
            }).catch(err => {
                log(`Failed to unsubscribe from ${url}: ${err}`);
            });
        }
    }

    private onConnected() {
        this.bindSocket();

        const urls = Array.from(this._subscriptions.keys());
        
        for (const url of urls) {
            Connection.get().send("stream", {
                url
            }).then(response => {
                this.onData(response);
            }).catch(err => {
                log(`Failed to resubscribe to ${url}: ${err}`);
            });
        }
    }

    private onDisconnected() {
        log('onDisconnected');
    }

    private bindSocket() {
        const socket = Connection.get().socket;
        if (this._boundSocket === socket) {
            return;
        }

        if (this._boundSocket) {
            this._boundSocket.removeListener('stream', this._streamListener);
        }

        socket.on('stream', this._streamListener);
        this._boundSocket = socket;
    }

    private onData(response : unknown) {
        const header = <StreamHeader>response;

        try {
            const subscription = this._subscriptions.get(header.url);
            if (subscription) {
                const data = uncompress(header.data);
                subscription.publish(data, header.hash);
            }
        } catch (e) {
            console.error(e);
        }
    }
}
